import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import { useGame } from '../context/GameContext';
import useWorkspace from '../hooks/useWorkspace';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiClock, FiImage, FiPlay } = FiIcons;
const HABIT_STORAGE_KEY = 'habitGarden.v3';

const localDate = (date = new Date()) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const paintingMinutes = (art) => {
  if (Number.isFinite(Number(art.length))) return Number(art.length);
  if (Number.isFinite(Number(art.durationMinutes))) return Number(art.durationMinutes);
  if (Number.isFinite(Number(art.durationMs))) return Number(art.durationMs) / 60000;
  return 0;
};

const completedDate = (art) => {
  const date = new Date(art.completedAt || art.createdAt || art.timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const knownHabitDates = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(HABIT_STORAGE_KEY) || 'null');
    if (!Array.isArray(saved?.habits)) return [];

    return saved.habits.flatMap((habit) => (
      Array.isArray(habit.completionHistory) ? habit.completionHistory : []
    ));
  } catch {
    return [];
  }
};

export default function Analytics() {
  const { state } = useGame();
  const { tasks } = useWorkspace();

  const gallery = useMemo(() => {
    const ids = new Set();
    return (state.gallery || []).filter((art) => {
      if (!art?.id || ids.has(art.id)) return false;
      ids.add(art.id);
      return true;
    });
  }, [state.gallery]);

  const week = useMemo(() => (
    Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() + index - 6);
      return {
        key: localDate(date),
        short: date.toLocaleDateString([], { weekday: 'short' }).slice(0, 1),
        label: date.toLocaleDateString([], {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        }),
      };
    })
  ), []);

  const summary = useMemo(() => {
    const days = Object.fromEntries(
      week.map((day) => [day.key, { minutes: 0, sessions: 0 }])
    );

    gallery.forEach((art) => {
      const date = completedDate(art);
      if (!date || !days[localDate(date)]) return;
      days[localDate(date)].minutes += paintingMinutes(art);
      days[localDate(date)].sessions += 1;
    });

    return {
      days,
      weeklyMinutes: Math.round(
        Object.values(days).reduce((total, day) => total + day.minutes, 0)
      ),
      weeklySessions: Object.values(days).reduce(
        (total, day) => total + day.sessions,
        0
      ),
      allMinutes: Math.round(
        gallery.reduce((total, art) => total + paintingMinutes(art), 0)
      ),
    };
  }, [gallery, week]);

  const completedTasks = tasks.filter((task) => task.status === 'completed').length;
  const habitDays = useMemo(() => new Set(knownHabitDates()).size, []);
  const maximum = Math.max(
    1,
    ...week.map((day) => summary.days[day.key].minutes)
  );
  const hasProgress = gallery.length || completedTasks || habitDays;

  return (
    <div className="max-w-6xl mx-auto space-y-7">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-5">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sage">
            Your effort, made visible.
          </p>
          <h1 className="text-4xl lg:text-5xl font-cormorant font-bold text-pine">
            Your progress
          </h1>
          <p className="mt-2 text-pine/65">
            Completed focus, paintings, tasks, and known habit check-ins.
          </p>
        </div>
        <div className="flex gap-2">
          <Button as={Link} to="/" variant="secondary">Today</Button>
          <Button as={Link} to="/studio">Studio</Button>
        </div>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          [FiClock, summary.weeklyMinutes, 'focused minutes this week', 'bg-sage/15'],
          [FiPlay, summary.weeklySessions, 'completed sessions this week', 'bg-apricot/15'],
          [FiImage, gallery.length, 'completed paintings', 'bg-lavender/15'],
          [FiCheckCircle, completedTasks, 'completed tasks', 'bg-skyblue/15'],
        ].map(([icon, value, label, tone]) => (
          <Card key={label} className={`p-5 ${tone}`}>
            <SafeIcon icon={icon} className="mb-3 text-pine/55" />
            <strong className="block text-3xl font-cormorant text-pine">{value}</strong>
            <span className="text-xs text-pine/60">{label}</span>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-8 p-6">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-cormorant font-bold text-pine">
                Focus this week
              </h2>
              <p className="text-sm text-pine/55">
                Minutes from completed, deduplicated gallery records.
              </p>
            </div>
            <p className="text-sm font-bold text-pine">{summary.weeklyMinutes} min</p>
          </div>

          {summary.weeklySessions ? (
            <div className="grid h-56 grid-cols-7 gap-2 items-end" aria-label="Focused minutes over seven days">
              {week.map((day) => {
                const minutes = summary.days[day.key].minutes;
                const height = minutes ? Math.max(10, (minutes / maximum) * 100) : 2;

                return (
                  <div key={day.key} className="flex h-full flex-col justify-end text-center">
                    <span className="mb-2 text-xs font-bold text-pine/55">
                      {minutes ? Math.round(minutes) : ''}
                    </span>
                    <div className="flex h-40 items-end rounded-2xl bg-paper px-1">
                      <div
                        title={`${day.label}: ${Math.round(minutes)} focused minutes`}
                        className={`w-full rounded-xl ${minutes ? 'bg-sage' : 'bg-pine/5'}`}
                        style={{ height: `${height}%` }}
                      />
                    </div>
                    <span className="mt-2 text-xs font-bold text-pine/45">{day.short}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-pine/15 p-10 text-center">
              <p className="font-bold text-pine">No completed focus sessions this week.</p>
              <p className="mt-1 text-sm text-pine/55">
                Completing a Studio session will add its real duration here.
              </p>
              <Button as={Link} to="/studio" className="mt-5">Try the studio</Button>
            </div>
          )}
        </Card>

        <div className="lg:col-span-4 space-y-4">
          <Card variant="pine" className="p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-paper/55">
              All completed focus
            </p>
            <strong className="mt-2 block text-4xl font-cormorant text-paper">
              {summary.allMinutes}
            </strong>
            <span className="text-sm text-paper/65">focused minutes</span>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-cormorant font-bold text-pine">Habit history</h2>
            {habitDays ? (
              <>
                <strong className="mt-3 block text-3xl text-pine">{habitDays}</strong>
                <p className="text-sm text-pine/55">
                  calendar days with at least one known check-in
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm text-pine/55">
                No dated habit check-ins are recorded yet. Older counts remain preserved
                without assigning invented dates.
              </p>
            )}
          </Card>
        </div>
      </div>

      {!hasProgress && (
        <Card className="p-8 text-center bg-paper">
          <h2 className="text-2xl font-cormorant font-bold text-pine">
            Your progress begins with your first completed step.
          </h2>
          <p className="mt-2 text-sm text-pine/60">
            Plan a task or finish a Studio session when you are ready.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button as={Link} to="/">Back to Today</Button>
            <Button as={Link} to="/studio" variant="secondary">Open Studio</Button>
          </div>
        </Card>
      )}
    </div>
  );
}